/**
 * Slice 10 — Owner home menu (CH3): Properties · Issues · Account.
 */
import { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useViewContext } from "@/contexts/ViewContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useTotalUnreadCount } from "@/hooks/useUnreadCounts";
import { useOwnerNotifications } from "@/hooks/useOwnerNotifications";
import { useProperties } from "@/hooks/useProperties";
import { useCohostProperties } from "@/hooks/useCohostProperties";
import { useIssuesInbox } from "@/hooks/useIssuesInbox";
import { isIssueOpen } from "@/lib/issueTriage";
import { cn } from "@/lib/utils";

interface MobileNavProps {
  title?: string;
}

export const MobileNav = ({ title: _title }: MobileNavProps) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const { viewContext } = useViewContext();
  const { data: totalUnread = 0 } = useTotalUnreadCount();
  const { data: notifications } = useOwnerNotifications();
  const unreadNotifications = notifications?.filter((n) => !n.is_read).length || 0;

  const { data: ownerProperties = [] } = useProperties();
  const { data: cohostProperties = [] } = useCohostProperties();
  const propertyIds = useMemo(() => {
    const ids = new Set<string>([
      ...ownerProperties.map((p) => p.id),
      ...cohostProperties.map((p) => p.id),
    ]);
    return [...ids];
  }, [ownerProperties, cohostProperties]);
  const { data: issues = [] } = useIssuesInbox(propertyIds.length ? propertyIds : undefined);
  const openIssueCount = issues.filter((i) => isIssueOpen(i.status)).length;
  const propertyCount = propertyIds.length;

  const handleNavigate = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const isCleanerChrome = viewContext === "cleaner";
  const initial = (profile?.full_name || profile?.email || "A").trim().slice(0, 1).toUpperCase();

  if (!user || !profile) return null;

  if (isCleanerChrome) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative md:hidden">
          <Menu className="h-5 w-5" />
          {(totalUnread > 0 || unreadNotifications > 0 || openIssueCount > 0) && (
            <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-destructive" />
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="left" hideClose className="flex w-[320px] flex-col border-[#e6edf1] bg-white p-0">
        <div className="flex items-center gap-3 px-5 pb-[22px] pt-6">
          <div className="flex h-[34px] w-[34px] items-center justify-center rounded-[10px] bg-[#24a89a] text-[15px] font-bold text-white">
            S
          </div>
          <div className="min-w-0 flex-1 text-base font-bold text-[#0f1720]">Cleaner Hub</div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f1f5f7] hover:bg-[#e6edf1]"
            aria-label="Close"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#0f1720" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-2 px-4 pb-6">
          <button
            type="button"
            onClick={() => handleNavigate("/owner/properties")}
            className={cn(
              "flex items-center justify-between gap-3.5 rounded-[18px] px-5 py-[22px] text-left",
              location.pathname.startsWith("/owner/properties") || location.pathname === "/owner/properties"
                ? "bg-[#e2eeed]"
                : "border-[1.5px] border-[#e6edf1]",
            )}
          >
            <div
              className={cn(
                "text-2xl font-bold tracking-[-0.6px]",
                location.pathname.startsWith("/owner/properties")
                  ? "text-[#15776d]"
                  : "text-[#0f1720]",
              )}
            >
              Properties
            </div>
            <div
              className={cn(
                "rounded-full px-3 py-1.5 text-[13px] font-bold",
                location.pathname.startsWith("/owner/properties")
                  ? "bg-white text-[#15776d]"
                  : "bg-[#f4f7f9] text-[#64748b]",
              )}
            >
              {propertyCount}
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleNavigate("/owner/issues")}
            className={cn(
              "flex items-center justify-between gap-3.5 rounded-[18px] px-5 py-[22px] text-left",
              location.pathname === "/owner/issues"
                ? "bg-[#e2eeed]"
                : "border-[1.5px] border-[#e6edf1]",
            )}
          >
            <div
              className={cn(
                "text-2xl font-bold tracking-[-0.6px]",
                location.pathname === "/owner/issues" ? "text-[#15776d]" : "text-[#0f1720]",
              )}
            >
              Issues
            </div>
            {openIssueCount > 0 ? (
              <div className="rounded-full bg-[#fdf3dd] px-3 py-1.5 text-[13px] font-bold text-[#8a6a15]">
                {openIssueCount}
              </div>
            ) : (
              <div className="rounded-full bg-[#f4f7f9] px-3 py-1.5 text-[13px] font-bold text-[#64748b]">
                0
              </div>
            )}
          </button>

          <button
            type="button"
            onClick={() => handleNavigate("/account")}
            className="mt-auto flex items-center gap-3 rounded-2xl bg-[#f4f7f9] px-[18px] py-4 text-left"
          >
            <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-[#e6edf1] text-[13px] font-bold text-[#64748b]">
              {profile?.avatar_url?.trim() ? (
                <img
                  src={profile.avatar_url.trim()}
                  alt=""
                  className="h-full w-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                initial
              )}
            </div>
            <div className="text-[15px] font-medium text-[#64748b]">Account</div>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
